import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LotteryListResponse from '../../../Api/SiteApi/Response/LotteryListResponse';
import SiteApi from '../../../Api/SiteApi/SiteApi';

import Config from '../../../Config/Config';
import LoadThemeComponent from '../../../base/LoadThemeComponent';
import { isRaffleType } from '../../../utils/LotteryHelpers';
import {
    isArabia,
    isBig5Lottery,
    isDefault,
    isDragonLotto24,
    isLottoAmerica,
    isLttry,
    isMML, isThankYouLotto
} from '../../../utils/template';

import ShowAllButton from '../../default/Buttons/ShowAllButton';

import Lottery from './Lottery';
import RaffleLottery from './RaffleLottery';

import { LotteryTypes } from 'components/play/Types/Enums';
import { renderComponent } from '../../../base/loadComponent';

type Props = Readonly<{ lotteryCount: string; isPlayPage?: string }>;

function LotteryList({ lotteryCount, isPlayPage }: Props) {
    const { t } = useTranslation();
    const [lotteryList, setLotteryList] = useState<LotteryListResponse[]>(
        Array(Number(lotteryCount) || 3).fill({
            name: null,
            type: LotteryTypes.Lottery,
            slug: null,
            currency_code: Config.get('app.currency'),
        })
    );
    const [loading, setLoading] = useState(true);
    const [hiddenComponent, setHiddenComponent] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const lotteryTypeFilter = searchParams.get('lottery_type');
    const defaultVisibleCount = 8;
    const [visibleCount, setVisibleCount] = useState(defaultVisibleCount);

    const handleLoadMore = () => {
        setVisibleCount(lotteryList.length);
    };

    useEffect(() => {
        SiteApi.lotteryList().then((response) => {
            if (response.data.length === 0) {
                setHiddenComponent(true);
            } else {
                let lotteries = Object.values(response.data);

                if (isPlayPage === '1') {
                    if (lotteryTypeFilter) {
                        lotteries = lotteries.filter((lottery) => lottery.type === lotteryTypeFilter);
                    } else {
                        lotteries = lotteries.filter((lottery) => !isRaffleType(lottery.type));
                    }
                }

                setLotteryList(lotteries);
            }
            setLoading(false);
        });
    }, [lotteryTypeFilter, isPlayPage]);

    let classes = '';
    switch (true) {
        case isMML():
        case isBig5Lottery():
        case isDragonLotto24():
        case isThankYouLotto():
            classes = 'col-xl-4 col-lg-6 col-12 mb-3';
            break;
        case isLttry():
        case isArabia():
            classes = isPlayPage === '1' ? 'col-md-6 col-12 mb-3' : 'col-xl-4 col-md-6 col-12 mb-3';
            break;
        case isLottoAmerica():
            classes = 'col-6 mb-3';
            break;
        case isDefault():
            classes = 'col-6 d-flex justify-content-center g-0';
            break;
        default:
            classes = 'col-md-6 col-12 mb-3';
            break;
    }

    function getLotteryBox(lottery: LotteryListResponse) {
        if (isRaffleType(lottery.type)) {
            return <RaffleLottery lottery={lottery} loading={loading} isPlayPage={isPlayPage} />;
        }

        return <Lottery lottery={lottery} loading={loading} isPlayPage={isPlayPage} />;
    }

    function getContent() {
        const baseView = () => {
            return (
                <div className={hiddenComponent ? 'd-none' : 'container play-lotteries-widget-wrapper'}>
                    <div className="play-lotteries play-lotteries-widget row flex-wrap">
                        {lotteryList.map((lottery, index: number) => {
                            return (
                                <div key={lottery.slug ?? index} className={classes}>
                                    {getLotteryBox(lottery)}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        };

        const defaultView = () => (
            <div className={hiddenComponent ? 'd-none' : 'container play-lotteries-widget-wrapper'}>
                <div className="play-lotteries play-lotteries-widget row flex-wrap">
                    {lotteryList.slice(0, visibleCount).map((lottery: LotteryListResponse) => getLotteryBox(lottery))}
                    {visibleCount < lotteryList.length && (
                        <div className="row show-more-lotteries justify-content-center g-0">
                            <ShowAllButton text={t('lotteryList.showAll')} onClick={handleLoadMore} />
                        </div>
                    )}
                </div>
            </div>
        );

        return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
    }

    return getContent();
}

renderComponent(LotteryList, 'lottery-list');
